import {JsonValue} from 'type-fest'

type NA<T> = Array<T | null> | null

export type ParsedProps<T = object> = T & Record<string, JsonValue>

export interface GenericMetaObject<
  P extends GenericMetaObject | never = GenericMetaObject<never>,
> {
  baseType: string
  isFree: boolean
  lang: null | string
  metaType: string
  name: string
  tizraId: string
  urlId: string
  pdfSourceFileName?: string
  props: ParsedProps<{
    BypassToC?: boolean
    hasOEmbed?: boolean
    CoverImage?: string | ParamFile
  }>
  // Not from the API, except on Excerpt. Attached by useMetaObj as needed.
  parent?: P
}

// baseType is optional because of TocEntry
export type UnknownMetaObject =
  | undefined
  | {baseType?: string; metaType: string}

const _isCollectionMetaType = (metaType?: string) =>
  !!metaType?.includes('Collection')

export const isCollectionMetaType = (
  metaType: string,
  searchTypes: SearchTypes | undefined, // require something to be passed
) =>
  _isCollectionMetaType(metaType) ||
  _isCollectionMetaType(searchTypes?.[metaType]?.baseType)

export const isCollection = (x: UnknownMetaObject): x is GenericMetaObject =>
  _isCollectionMetaType(x?.metaType) || _isCollectionMetaType(x?.baseType)

export type Excerpt<P extends GenericMetaObject = GenericMetaObject> =
  GenericMetaObject & {
    metaType: 'PageRange'
    pages: [number, number]
    // Excerpts always include the parent object, whether from /api/query or
    // /api/search. There is no bookTizraId on an Excerpt, so there's no way to
    // find the parent if it were omitted.
    parent: P
  }

const _isExcerptMetaType = (metaType?: string) => metaType === 'PageRange'

export const isExcerptMetaType = (
  metaType: string,
  searchTypes: SearchTypes | undefined, // require something to be passed
) =>
  _isExcerptMetaType(metaType) ||
  _isExcerptMetaType(searchTypes?.[metaType]?.baseType)

export const isExcerpt = (x: UnknownMetaObject): x is Excerpt =>
  _isExcerptMetaType(x?.metaType) || _isExcerptMetaType(x?.baseType)

export interface PdfPage extends GenericMetaObject {
  metaType: 'PdfPage'
  bookTizraId: string
  logicalPageNumber: string
  pageNumber: number
}

const _isPdfPageMetaType = (metaType?: string) => metaType === 'PdfPage'

export const isPdfPage = (x: UnknownMetaObject): x is PdfPage =>
  _isPdfPageMetaType(x?.metaType) || _isPdfPageMetaType(x?.baseType)

export interface User extends GenericMetaObject {
  email: string
  isAdmin: boolean
  metaType: 'UserData'
  name: string
}

export interface TocEntry<P extends GenericMetaObject = GenericMetaObject> {
  id: string
  bookTizraId: string
  metaType: 'toc-entry'
  logicalPage: string
  pageNumber: number
  title: string
  level: number
  isDisplayed: boolean
  // Not from the API. Attached by useMetaObj as needed.
  parent?: P
}

export interface NestedTocEntry<P extends GenericMetaObject = GenericMetaObject>
  extends TocEntry<P> {
  hasChildren: boolean
  children: NestedTocEntry<P>[]
}

export const isTocEntry = (x: UnknownMetaObject): x is TocEntry =>
  x?.metaType === 'toc-entry'

export interface Termsheet {
  duration: string
  endDate: string | null
  excludedViews: string[]
  isExpired: boolean
  startDate: string | null
}

export interface License {
  active: boolean
  controlled: string
  creationDate: string
  id: number
  terms: Termsheet
}

export interface Licenses {
  allUserSets?: NA<{
    licenses?: NA<License>
  }>
  licenseBindings?: NA<{
    licenses?: NA<License>
  }>
  samplingLicenses?: NA<unknown>
  sessionLicenses?: Record<string, License | null>
  userLicenses?: NA<{
    user?: User | null
    userLicenseInfo?: null | {
      userlicenses?: NA<License>
    }
  }>
}

export interface Offer extends GenericMetaObject {
  controlled: {
    baseType: string
    metaType: string
    tizraId: string
  }
  currencyInfo: string
  metaType: 'Offer'
  price: number
  termSheet: Termsheet
}

export type MetaObject = Excerpt | Offer | PdfPage | User | GenericMetaObject

export type SearchResultTypes = MetaObject | TocEntry

export type SearchResult<T = SearchResultTypes> = T & {
  snippet?: string
}

export interface CartItem {
  item: MetaObject
  offer: Offer
}

export interface Checkouts {
  checkouts: CheckoutsCheckout[]
  defaultCheckout?: string
}

export interface CheckoutsCheckout {
  displayName: string
  name: string
}

export type CheckoutStatus = 'complete' | 'error' | 'incomplete'

export interface Checkout extends CheckoutsCheckout {
  cart: CartItem[]
  checkoutStatus: CheckoutStatus
  infoItems: InfoItem[]
  steps: CheckoutStep[]
  nonce: string
}

// Not really complete but sufficient for UMPH, maybe.
export interface InfoItem {
  type: 'sub-total' | 'sales-tax' | 'total' | unknown
  message: string
  quantity?: {
    value: number
    format?: {
      style: 'decimal' | 'currency' | 'percent' | 'unit'
      // TODO: https://en.m.wikipedia.org/wiki/ISO_4217
      currency?: string // ISO 4217 e.g. 'USD' if style==currency
      // TODO: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat/NumberFormat#unit_2
      unit?: unknown // if style==unit
      minimumFractionDigits?: number
      maximumFractionDigits?: number
      // TODO: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat/NumberFormat#roundingmode
      roundingMode?: unknown
    }
  }
}

export interface CheckoutStep {
  name: string
  buttonPrompt?: string | null
  displayName?: string
  final?: boolean
  hidden?: boolean
  infoPrompt?: string
  infoRequired?: CheckoutField[]
  optional?: boolean
  status?: CheckoutStatus
  errors?: ReasonableError[]
}

export interface CheckoutField {
  name: string
  prompt?: string
  type?: 'string' | 'integer' | 'boolean'
  validationType?: string
  optional?: boolean
  defaultValue?: any
  options?: Array<{
    value: string
    label?: string
    disabled?: boolean
  }>
  error?: ReasonableError
  status?: CheckoutStatus
}

export interface CheckoutSubmit {
  transaction: {
    applications: OfferApplication[]
    label: string
    id: number
    status: 'OPEN' | 'CLOSED' | 'PENDING' | 'FAILED' | 'CANCELLED'
    start: string
    close: string | null
    userId: number
  }
  transactionData: {
    transactionAmountDouble: number
    transactionTaxAmountDouble: number
    taxCountry?: string
    taxState?: string
    taxCity?: string
    zipOrRegionCode?: string
  }
  transactionId: number

  // Currently we have two kinds of submit successes: status=OPEN with a way to
  // continue (paypal redirect) and status=CLOSED with no additional
  // information.
  type?: 'redirect'
  method?: 'GET'
  url?: string
}

export interface OfferApplication extends Offer {
  price: number
  offer: Offer
}

export interface ReasonableError {
  reason: string
  message: string
}

export interface FormErrors extends Partial<ReasonableError> {
  errors?: Record<string, ReasonableError>
  status?: number
}

export type PropType =
  | 'auto-uuid'
  | 'boolean-list' // keyword checklist
  | 'css-color'
  | 'date'
  | 'date-list'
  | 'float'
  | 'float-list'
  | 'html'
  | 'integer'
  | 'integer-list'
  | 'ISBN'
  | 'ISBN-13'
  | 'json-array'
  | 'json-hash'
  | 'json-value'
  | 'keyword'
  | 'keyword-list'
  | 'markdown'
  | 'param-file'
  | 'reference'
  | 'reference-list'
  | 'string'
  | 'string-list'
  | 'text'

export interface PropDef {
  displayName: string
  inheritValue: boolean
  isCalculated: boolean
  isConstrained: boolean
  isJsonVisible: boolean
  isSearchable: boolean
  isSystem: boolean
  isUserDefined: boolean
  isUserVisible: boolean
  lang: unknown
  metaTagExternalName: string
  name: string
  sortField: string
  type: PropType
  valueSpecification: string
  _from?: string // internal bookkeeping
}

export interface TypeInfo {
  // In theory we could remove allSubtypes and subtypes in the SDK layer as we
  // do for superType and supertypes, but that theory relies on all types being
  // present in the API response so we can fully construct the graph from
  // superTypeAncestry. The problem is that not all types are present, in
  // particular VirtualCollectionInstance is missing because it is marked
  // internal, so the only way we know that it exists (and is therefore valid
  // for browse) is by its presence in AdminTagged allSubtypes.
  // https://github.com/Tizra/cubchicken/issues/1028
  allSubtypes: string[]
  baseType: string
  baseTypeAncestry: string[]
  displayName: string
  displayNamePlural: string
  displayNameSingular: string
  id: number
  isInternal: boolean
  isSystemDefinition: boolean
  isUserDefined: boolean
  metaSourceNames: string[]
  name: string
  namePropName: string
  propDefs: {[k: string]: PropDef}
  subtypes: string
  superTypeAncestry?: string[] // AdminTagged lacks
  // We remove all of these in our SDK layer because they duplicate other
  // properties.
  //superType: string
  //supertypes: string[]
  //tagDefinitions: {[k: string]: PropDef}
}

export type TypeInfos = {[k: string]: TypeInfo}

export interface SearchType extends Omit<TypeInfo, 'propDefs'> {
  propDefsIncludingSubtypes: {[k: string]: PropDef}
  // We remove all of these in our SDK layer because they duplicate other
  // properties.
  //allSubtypes: string[]
  //subtypes: string
  //superType: string
  //supertypes: string[]
  //tagDefinitionsIncludingSubtypes: {[k: string]: PropDef}
}

export type SearchTypes = {[k: string]: SearchType}

export interface Attachment {
  contentType: string
  dateUploaded?: string
  name: string
  numberOfPages?: number
  props: ParsedProps<{
    DisplayName?: string
    includeDirectly?: boolean
    isDownload?: boolean
    isVisible?: boolean
    isUrlName?: boolean
    target?: string
  }>
  size?: number
  sortField?: string
  sourceName: string
  url: string
}

export type ParamFile = Omit<Attachment, 'sourceName'> | {default: string}

export type Paged<T> = {
  result: T[]
  size: number
  next?: string
}

export interface AuthInfo {
  authInfo: string
  objectDefaultViewConfig: {
    free: string[]
    restricted: string[]
  }
  sessionViewConfig: {
    free: string[]
    restricted: string[]
  }
}

export type OEmbed = {
  html?: string
  thumbnail_url_with_play_button?: string
  thumbnail_url?: string
}

export type Redemption = {
  licensesAdded?: License[]
  sessionLicensesAdded?: License[]
  tagsAdded?: string[]
  user: User
}

export type PubInfo = {
  detailUrl: string
  numPages: number
  title: string
  tizraId: string
  urlId: string
}

export type PdfPageInfo = {
  imageUrl: string // "/9sh/cdn-2023-04-24 14:36:00.236/joy/1.jpg",
  xmlUrl: never
  // I don't trust this API, so everything after is optional
  attachmentsUrl?: string // "/9sh/cdn-2023-04-24 14:36:00.236/joy/1.attachments-list"
  extractedTextUrl?: string // "/9sh/cdn-2023-04-24 14:36:00.236/joy/1.extracted-text",
  imageLinks?: Array<{
    anchorText?: string | null
    destination: string
    destinationType:
      | 'anchor_ref'
      | 'attachment_ref'
      | 'logical_page'
      | 'old_style'
      | 'page_ref'
      | 'print_page'
      | 'script'
      | 'url_ref'
    htmlClass?: string | null
    linkRel?: string | null
    linkTitle?: string | null
    pageNum?: number | null // for destinationType page_ref
    rectangleList: Array<{
      top: number
      left: number
      height: number
      width: number
    }>
    target?: string | null
  }>
  searchHighlightUrl?: string // "/api/highlighted-value?tizra-id=btc&page-num=1"
}

export type XmlPageInfo = {
  imageUrl: never
  xmlUrl: string // "/9sh/cdn-2023-03-07 19:32:31.254/k9dd/1.xml"
  // I don't trust this API, so everything after is optional
  attachmentsUrl?: string // "/9sh/cdn-2023-04-24 14:36:00.236/joy/1.attachments-list"
}

export type PageInfo = PdfPageInfo | XmlPageInfo

export interface AdminMetaObject extends GenericMetaObject {
  hasUnpublishedChanges: boolean
  liveDate: string
  modifiedDate: string
  publishedLive: boolean
  publishedStaging: boolean
  stagingDate: string
}

export interface AdminType {
  allPropName: string[]
  allSubtypes: string[]
  baseType: string
  baseTypeAncestry: string[]
  displayName: string
  displayNamePlural: string
  displayNameSingular: string
  id: number
  isInternal: boolean
  isSystemDefinition: boolean
  isUserDefined: boolean
  metaSourceNames: string[]
  name: string
  namePropName: string
  propDefs: {[k: string]: string}
  subtypes: string[]
  superTypeAncestry: string[]
  supertypes: string[]
}

export type Nullish = null | undefined

export const nullish = (x: unknown): x is Nullish =>
  x === null || x === undefined

export const notNullish = <T>(x: T | Nullish): x is T => !nullish(x)

export type Falsish = Nullish | false

export const falsish = (x: unknown): x is Falsish => nullish(x) || x === false

export const truish = <T>(x: T | Falsish): x is T => !falsish(x)

export type Falsy = Falsish | 0 | 0n | ''

export const falsy = (x: unknown): x is Falsy => !x

export const truthy = <T>(x: T | Falsy): x is T => !falsy(x)

/**
 * Any object.
 */
type AnyObject = Record<string | symbol, unknown>

export const isObjectType = (x: unknown): x is object =>
  typeof x === 'object' && x !== null

export const isObject = (x: unknown): x is AnyObject =>
  Object.prototype.toString.call(x) === '[object Object]'

export const isPlainObject = (x: unknown): x is AnyObject => {
  // https://github.com/jonschlinkert/is-plain-object/blob/master/is-plain-object.js
  if (!isObject(x)) return false
  const ctor = (x as any).constructor
  if (ctor === null || ctor === undefined) return true
  const proto = (ctor as any).prototype
  if (!isObject(proto)) return false
  if (!Object.hasOwn(proto, 'isPrototypeOf')) return false
  return true
}

export const isEmptyArr = (x: unknown) =>
  !!x && Array.isArray(x) && x.length === 0

/**
 * Check whether `prop` is an own property of `obj` or not.
 */
export function hasOwnProperty<T extends object>(
  object: T,
  prop: keyof any,
): prop is keyof T {
  return Object.hasOwn(object, prop)
}

export const isThenable = <T>(x: unknown): x is Promise<T> =>
  typeof (x as any)?.then === 'function'

/**
 * Identity function that kills TS object spread complaints.
 */
export const butter = <T>(x: boolean | null | undefined | T) => x as T
